import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { useTranslator } from '@/hooks/useTranslator';
import { AnnouncementStep as AnnouncementStepType } from '@/types/announcement';

interface Props {
  readonly step: AnnouncementStepType;
}

/**
 * Parses basic markdown-style formatting into React elements.
 * Supports **bold** and *italic*.
 */
const parseFormattedText = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const regex: RegExp = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let lastIndex: number = 0;
  let match: RegExpExecArray | null = regex.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[2]) {
      parts.push(<strong key={match.index}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={match.index}>{match[3]}</em>);
    }

    lastIndex = match.index + match[0].length;
    match = regex.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

export const AnnouncementStep: React.FC<Props> = (props: Props): React.ReactElement => {
  const { step } = props;
  const tr = useTranslator();
  const [imageError, setImageError] = React.useState(false);

  React.useEffect((): void => {
    setImageError(false);
  }, [step.imageUrl]);

  return (
    <Box>
      {step.imageUrl && !imageError && (
        <Box sx={styles.imageContainer}>
          <Box
            component="img"
            src={step.imageUrl}
            alt=""
            onError={(): void => setImageError(true)}
            sx={styles.image}
          />
        </Box>
      )}
      <Typography variant="h5">{tr(step.title)}</Typography>
      <Box mt={1}>
        <Typography variant="body2" color="custom.LightGray" whiteSpace="pre-line">
          {parseFormattedText(tr(step.body))}
        </Typography>
      </Box>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    mb: 3,
  },
  image: {
    maxWidth: '100%',
    maxHeight: { sm: 240, xs: 180 },
    objectFit: 'contain',
  },
};
